import 'stream-chat-react/dist/css/v2/index.css';

import { Button, Icon, Stack, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import { ChannelRole, Thread, ThreadStatus, ThreadUser } from '@schooly/api';
import { ChannelHeader, ChannelPreview, Message, useThreads } from '@schooly/components/threads';
import { ClosedThreadIcon } from '@schooly/style';
import { FC, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  DefaultStreamChatGenerics,
  MessageInput,
  StreamMessage,
  useChannelActionContext,
  VirtualizedMessageList,
} from 'stream-chat-react';

import { getRouteModalPathname } from '../../../../helpers/misc';

type MessageChannelProps = {
  connectedUser: ThreadUser;
  onMessageEdit?: (message: StreamMessage<DefaultStreamChatGenerics>) => void;
  hideChannelPreview?: boolean;
};

export const MessageChannel: FC<MessageChannelProps> = ({
  connectedUser,
  onMessageEdit,
  hideChannelPreview,
}) => {
  const { jumpToFirstUnreadMessage, markRead } = useChannelActionContext();
  const { addMember, selectThread, streamChannel, selectedThread, updateThreadStatus } =
    useThreads();
  const { formatMessage } = useIntl();

  useEffect(() => {
    const unread = streamChannel?.state.read[connectedUser.user_id]?.unread_messages;
    if (!!unread) {
      jumpToFirstUnreadMessage();
    }
  }, [connectedUser.user_id, jumpToFirstUnreadMessage, streamChannel]);

  useEffect(() => {
    if (streamChannel) {
      const subscription = streamChannel.on('message.read', async () => {
        markRead();
      });

      return subscription.unsubscribe;
    }
  }, [markRead, streamChannel]);

  const isSpectator = selectedThread?.channel?.members.some(
    (s) => s.user.user_id === connectedUser.user_id && s.role === ChannelRole.CHANNEL_SPECTATOR,
  );

  const onJoinChannel = useCallback(async () => {
    if (!selectedThread) return;
    const updatedThread = await addMember(selectedThread, connectedUser);

    trackAmplitudeEvent({
      name: AnalyticsEventName.PublisherJoinsThread,
      payload: { thread_id: selectedThread.id, user_id: connectedUser.user_id },
    });

    selectThread(updatedThread);
  }, [addMember, connectedUser, selectThread, selectedThread]);

  const handleDelete = useCallback(<T extends { id: string }>(message: T) => {
    trackAmplitudeEvent({
      name: AnalyticsEventName.DeleteThreadMessage,
      payload: { message_id: message.id, deleted_at: new Date().toISOString() },
    });
  }, []);

  const isThreadClosed = !!streamChannel
    ? !!streamChannel?.data?.frozen
    : selectedThread?.status === ThreadStatus.ARCHIVED;

  const handleThreadStatusUpdate = useCallback(
    async (thread: Thread, status: ThreadStatus) => {
      await updateThreadStatus(thread, status);
    },
    [updateThreadStatus],
  );

  const messageInput = isSpectator ? (
    <Stack alignItems="center" justifyContent="center">
      <Button onClick={onJoinChannel}>{formatMessage({ id: 'messages-ThreadChannelJoin' })}</Button>
    </Stack>
  ) : (
    <MessageInput />
  );

  if (!selectedThread) return null;

  return (
    <Stack height="100%" width="100%">
      <ChannelHeader
        isThreadClosed={isThreadClosed}
        thread={selectedThread}
        onThreadStatusUpdate={handleThreadStatusUpdate}
      />
      <VirtualizedMessageList
        Message={() => <Message onDelete={handleDelete} onEdit={onMessageEdit} />}
        disableDateSeparator={false}
        additionalVirtuosoProps={
          hideChannelPreview
            ? undefined
            : {
                components: {
                  Header: () => (
                    <ChannelPreview thread={selectedThread} getRoutePath={getRouteModalPathname} />
                  ),
                },
              }
        }
      />
      {isThreadClosed ? (
        <Stack py={2} justifyContent="center" gap={1} direction="row">
          <Icon sx={{ color: 'common.grey' }}>
            <ClosedThreadIcon />
          </Icon>
          <Typography variant="h3" color="common.grey2" textAlign="center">
            {formatMessage({ id: 'messages-ThreadIsClosed' })}
          </Typography>
        </Stack>
      ) : (
        messageInput
      )}
    </Stack>
  );
};
